import './App.css';
import { Bg, Main, MainMobile, ResultMobile } from '@/components';
import WebsiteHeader from 'fotor-website-header/websiteHeaderNoCss.bundle';
import { Suspense, lazy, useEffect } from 'react';
import { 
	Navigation,
	RemainCredits,
} from '@/components';
import { useCommonStore, useUserStore } from './stores';
import { getSafariInfo, loadMobileCommonScript, mobileCommonSdkLoaded, setCookie } from './utils';
import { ToastContainer, Bounce } from 'react-toastify';
import { useIsMobile } from '@/hooks';
import { I18nLangMap, LOGIN_SCENE, POSITION, PROJECT_TYPE, TaskStatus } from './constants';
import { useTranslation } from 'react-i18next';
import FTAnalytic from '@/analyze/FTAnalytic';

const ImgEditor = lazy(() => import("@/components/img-editor"));
const VideoEditor = lazy(() => import("@/components/video-editor"));

const App = () => {
	const { i18n } = useTranslation();
	const isMobile = useIsMobile();
	const imgEditorOptions = useCommonStore(state => state.imgEditorOptions);
	const videoEditorOptions = useCommonStore(state => state.videoEditorOptions);
	const task = useCommonStore(state => state.task);
	const setIsMobile = useCommonStore(state => state.setIsMobile);
	const logoutCallback = useCommonStore(state => state.logoutCallback);
	const fetchCredits = useUserStore(state => state.fetchCredits);
	const setUserInfo = useUserStore(state => state.setUserInfo);
	const isLogin = useUserStore(state => state.getters.isLogin);
	const fetchUserInfo = useUserStore(state => state.fetchUserInfo);
	const fetchVipStatus = useUserStore(state => state.fetchVipStatus);
	const resetUserStore = useUserStore(state => state.reset);
	const setIsPlayResult = useCommonStore(state => state.setIsPlayResult);
	const updatePlayState = useCommonStore(state => state.updatePlayState);
	const fetchPresetExpTempList = useCommonStore(state => state.fetchPresetExpTempList);
	const applyPresetExp = useCommonStore(state => state.applyPresetExp);
	const setSafariInfo = useCommonStore(state => state.setSafariInfo);

  const headerCallback = (data: any) => {
		let { type } = data;

		if (type === 'logout') {
			logoutCallback();
			resetUserStore();
		} else if (type === 'rendered') {
		} else if (type === 'login') {
			if ([0, 1, 2, 3, 5, 6].includes(data.res.code)) {
				setUserInfo(data.res.userInfo.data);
				fetchVipStatus();
				fetchCredits();
			}
		} else if (type === "language") {
			const localeKey = data.value || "en_US";
			const i18nKey = Object.keys(I18nLangMap).find(key => I18nLangMap[key] === localeKey);
			i18n.changeLanguage(i18nKey);
			if (!["id"].includes(i18nKey!)) {
				setCookie("locale", localeKey);
			}
		}
	};

	useEffect(() => {
    (async () => {
			await fetchPresetExpTempList();
			applyPresetExp();
		})()
  }, []);

	useEffect(() => {
		setIsMobile(isMobile);
	}, [isMobile]);

	useEffect(() => {
		loadMobileCommonScript().then(() => {
			/** @补充移动进入弹窗 **/
			mobileCommonSdkLoaded(() => {
			const inst = window.mobileCommon && window.mobileCommon.EntryPopupInstance();
			inst.show({
				projectType: "video-mimic"
			});
			})
		})
	}, []);

	useEffect(() => {
		(async () => {
			await fetchUserInfo();
			await fetchVipStatus();
			await fetchCredits();
		})();
	}, []);

	useEffect(() => {
		const visibilitychange = () => {
			if (document.visibilityState === 'visible') {
				// todo
			} else {
				setIsPlayResult(false);
				updatePlayState(false);
			}
		}
		document.addEventListener('visibilitychange', visibilitychange);

		return () => {
			document.removeEventListener('visibilitychange', visibilitychange);
		}
	}, []);

	// 埋点（进入工具）
	useEffect(() => {
		FTAnalytic.addAnalytic({
			type: 'ev',
			action: 'fotor_web$enter$swap_project_page',
			label: {
				location: POSITION,
				project_type: PROJECT_TYPE,
			},
		});
	}, []);

	useEffect(() => {
		setSafariInfo(getSafariInfo());
	}, [])

	useEffect(() => {
		i18n.on("languageChanged", fetchPresetExpTempList);
	}, [])

  return (
    <div className="app">
      <Bg></Bg>

      <WebsiteHeader
        style={{
          background: "#FFFFFF",
					boxShadow: "0px 1px 0px 0px rgba(0, 0, 0, 0.1)",
          position: "static"
        }}
				displayLanguageMenu={[
          "en",
					"es",
					"pt",
					"ru",
					"fr",
					"de",
					"cn",
					"tw",
					"jp",
        ]}
				showOpenInApp={!isLogin? true:false  }
				projectType="video-mimic"
				noNavMenu={false}
				lang={I18nLangMap[i18n.language]}
				callback={headerCallback}
				noLanguage={true}
				ShowUpgradeButton={false}
				theme="white"
				env={process.env.REACT_APP_ENVIRONMENT}
				loginSensorsData={[
					{ position: "header" },
					true,
					{ scenesId: LOGIN_SCENE },
				]}
				signUpSensorData={[
					{ position: "header" },
					true,
					{ scenesId: LOGIN_SCENE },
				]}
				pageType="tool"
				showCredits={false}
			>
				{isMobile === false && (
					<Navigation
					></Navigation>
				)}
				{isLogin && (
					<RemainCredits
					></RemainCredits>
				)}
			</WebsiteHeader>

      {isMobile === true && (
				<>
					{task && task.status === TaskStatus.Success ? (
						<ResultMobile></ResultMobile>
					) : (
						<MainMobile></MainMobile>
					)}
				</>
			)}

			{isMobile === false && (
				<Main></Main>
			)}

			{imgEditorOptions.show && (
				<Suspense>
					<ImgEditor></ImgEditor>
				</Suspense>
			)}

			{videoEditorOptions.show && (
				<Suspense>
					<VideoEditor></VideoEditor>
				</Suspense>
			)}

			<ToastContainer
				position="top-center"
				autoClose={1500}
				hideProgressBar
				newestOnTop={false}
				rtl={false}
				pauseOnFocusLoss
				draggable
				pauseOnHover
				theme="colored"
				transition={Bounce}
				closeButton={false}
				limit={3}
				className="custom_toast_container"
				toastClassName="custom_toast"
				bodyClassName="custom_toast_body"
			/>
    </div>
  );
}

export default App;
